import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import moment from "moment";
import { message, notification } from "antd";

import { IUseSuitabilityTest } from "./IUseSuitabilityTest";

import { emptyTestResponses, emptyTestResponsesErrors } from "../../Assets/Data/EmptyValues";
import { Contract, SuitabilityTestErrors, SuitabilityTestInvestor, SuitabilityTestResult } from "../../Models";

export const useSuitabiltyTest = ({
	suitabilityTestAPI,
	unlockNextStep,
	navigate,
	investorAPI,
	contractAPI,
	translations,
}: IUseSuitabilityTest) => {
	const [searchParams] = useSearchParams();
	const [idContract, setIdContract] = useState<number>(0);
	const [contract, setContract] = useState<Contract>();
	const [investorInfo, setInvestorInfo] = useState<SuitabilityTestInvestor>({
		idInvestorTestFiller: undefined,
		name: undefined,
		surname: undefined,
		birthDate: undefined,
		dni: undefined,
	});

	const [isSuitabilityDone, setIsSuitabilityDone] = useState<boolean>(false);

	const [testResponses, setTestResponses] = useState<SuitabilityTestResult>(emptyTestResponses);
	const [errors, setErrors] = useState<SuitabilityTestErrors>(emptyTestResponsesErrors);
	const [isOpenConfirmModal, setIsOpenConfirmModal] = useState<boolean>(false);

	useEffect(() => {
		if (searchParams.has("ContractId")) {
			setIdContract(Number(searchParams.get("ContractId")));
		} else if (searchParams.has("hash")) {
			const hash = searchParams.get("hash");
			const getContractIdByHash = async (hash: string) => {
				return await contractAPI.getContractByHash(hash)
					.then((res: any) => {
						if (res.success) {
							setIdContract(Number(res.data));
						}
					});
			};

			getContractIdByHash(hash!);
		}
	}, [translations]);

	useEffect(() => {
		if (idContract && idContract > 0) {
			contractAPI.getContract(idContract)
				.then((res: any) => {
					if (res.success) {
						setContract(res.data);
						suitabilityTestAPI.getSuitabilityTest(idContract)
							.then(async (res: any) => {
								if (res.success && res.data?.idContract) {
									setIsSuitabilityDone(true);
									setTestResponses({
										...emptyTestResponses,
										...res.data,
										dateOfCompletion: res.data.dateOfCompletion
											? moment(res.data.dateOfCompletion).format("YYYY-MM-DD")
											: null,
									});
								} else {
									setTestResponses((prevState: SuitabilityTestResult) => ({
										...prevState,
										dateOfCompletion: null,
									}));
								}
								await getInvestorData(idContract);
							});
					}
				});
		}
	}, [idContract]);

	const getInvestorData = async (contractId: number) => {
		await investorAPI.getInvestors(contractId).then((res: any) => {
			if (res.success && res.data.length > 0) {
				setContract((prevState) => prevState ? ({
					...prevState,
					commitment: Number(res.data[0].commitment ?? prevState.commitment ?? 0),
				}) : prevState);

				setInvestorInfo({
					name: res.data[0].name,
					surname: res.data[0].lastName,
					birthDate: res.data[0].birthDate
						? moment(res.data[0].birthDate).format("YYYY-MM-DD")
						: undefined,
					dni: res.data[0].dni || res.data[0].nif || res.data[0].passport || res.data[0].residenceCard,
					idInvestorTestFiller: res.data[0].id,
				});
			}
		});
	};

	const isTestDataValid = () => {
		let isOK = true;
		if (!isSuitabilityDone) {
			if (!testResponses.dateOfCompletion) {
				setErrors((prevState: any) => ({ ...prevState!, dateOfCompletion: true }));
				isOK = false;
			}

			if (
				!testResponses.q1_1 &&
				!testResponses.q1_2 &&
				!testResponses.q1_3 &&
				!testResponses.q1_4 &&
				!testResponses.q1_5
			) {
				setErrors((prevState: any) => ({ ...prevState!, question1: true }));
				isOK = false;
			}
		}

		if (!isSuitabilityDone) {
			for (let key in testResponses) {
				if (testResponses[key] === null || testResponses[key] === undefined) {
					if (key.includes("Vol")) {
						const keyOps = key.replace("Vol", "Ops");
						if (testResponses[keyOps] !== null && testResponses[keyOps] !== 3) {
							setErrors((prevState: any) => ({ ...prevState!, [key]: true }));
							isOK = false;
						}
					} else if ((key === 'q9' && isContinuationFinance()) || key !== 'q9') {
						setErrors((prevState: any) => ({ ...prevState!, [key]: true }));
						isOK = false;
					}
				}
			}
		}

		return isOK;
	};

	const saveTest = () => {
		let result = isTestDataValid();
		if (result) {
			let data = {
				...testResponses,
				dateOfCompletion: testResponses.dateOfCompletion ? moment(testResponses.dateOfCompletion).format("YYYY-MM-DD") : null,
				idContract,
				idInvestorTestFiller: investorInfo.idInvestorTestFiller,
				suitable: false,
			};
			suitabilityTestAPI.saveSuitabilityTest(data).then((res: any) => {
				if (res.success) {
					if (res.data) {
						toNextStep();
						message.success(translations.suitabilityTestSent);
					} else {
						setIsOpenConfirmModal(true);
					}
				}
			});
		} else {
			notification.error({
				message: "You should fill in all the fields in the form",
			});
		}
	};

	const onQuestionAnswered = (question: string) => (response: any) => {
		if (question.startsWith("Q1")) {
			setErrors((prevState: any) => ({ ...prevState!, Q1: false }));
		} else {
			setErrors((prevState: any) => ({ ...prevState!, [question]: false }));
		}

		setTestResponses((prevState: SuitabilityTestResult | undefined) => ({
			...prevState!,
			[question]: response,
		}));
	};
	
	const isContinuationFinance = () => {
		return contract?.isContinuationFinance === true;
	};

	const getQ4Title = () => {
		if (isContinuationFinance()) {
			return translations.suitability_q4_continuation_finance;
		} else {
			return translations.suitability_q4;
		}
	};


	const onCancelContinue = () => {
		setIsOpenConfirmModal(false);
		navigate("/");
	};

	const toNextStep = () => {
		setIsOpenConfirmModal(false);
		unlockNextStep(idContract);
	};

	return {
		contract,
		isSuitabilityDone,
		errors,
		investorInfo,
		testResponses,
		isOpenConfirmModal,
		contractId: idContract,
		onQuestionAnswered,
		saveTest,
		isContinuationFinance,
		getQ4Title,
		onCancelContinue,
		toNextStep,
	};
};
